<div class="login-container">
  <div class="logo" [routerLink]="['/home']" *ngIf="!serverUnavailable && !showPleaseWait && !foundError">
    <img [src]="logoImageUrl" alt="Zum Connect Logo">
  </div>

  <div class="content-wrapper">
    <!-- Login form -->
    <div class="login-card" *ngIf="!serverUnavailable && !showPleaseWait && !foundError">
      <div class="login-card__header">
        <h1 class="login-card__title">
          <span class="login-card__welcome">{{ 'login.HelloWelcomeTo' | translate }}</span>
          <span class="login-card__brand">{{ 'auth.Brand' | translate }}</span>
        </h1>
        <p class="login-card__subtitle">
          <span *ngIf="loginStep !== 'credentials'">{{ challengeTitleKey | translate }}</span>
          <span *ngIf="loginStep === 'credentials'">{{ (isPasswordless ? 'login.PasswordlessSteps' : 'auth.Steps') | translate }}</span>
        </p>
      </div>

      <div class="login-card__errors" *ngIf="noticeMessage">
        <div class="login-card__error login-card__error--info">
          <i-feather name="info" class="login-card__error-icon"></i-feather>
          {{ noticeMessage }}
        </div>
      </div>

      <div class="login-card__errors" *ngIf="backendError">
        <div class="login-card__error">
          <i-feather name="alert-circle" class="login-card__error-icon"></i-feather>
          {{ backendError }}
        </div>
      </div>

      <!-- Credentials -->
      <form
        class="login-card__form"
        *ngIf="loginStep === 'credentials'"
        [formGroup]="loginForm"
        (ngSubmit)="login(loginForm.value)">
        <div class="login-card__fields-block">
          <div class="login-card__fields">
            <div class="input-field"
              [class.input-field--error]="loginForm.get('email').invalid && (loginForm.get('email').dirty || loginForm.get('email').touched || submitted)">
              <label class="input-field__label" for="email">{{ 'login.EMAIL' | translate }}</label>
              <div class="input-field__control">
                <i-feather name="mail" class="input-field__icon"></i-feather>
                <input
                  type="email"
                  id="email"
                  class="input-field__input"
                  formControlName="email"
                  [placeholder]="'login.EmailPlaceholder' | translate"
                  required>
              </div>
              <p class="input-field__helper input-field__helper--error"
                *ngIf="loginForm.get('email').invalid && (loginForm.get('email').dirty || loginForm.get('email').touched || submitted)">
                <span *ngIf="loginForm.get('email').hasError('required')">{{ 'login.EmailIsRequired' | translate }}</span>
                <span *ngIf="loginForm.get('email').hasError('email') && !loginForm.get('email').hasError('required')">
                  {{ 'login.EmailIsValid' | translate }}
                </span>
              </p>
            </div>

            <div class="input-field"
              *ngIf="!isPasswordless"
              [class.input-field--error]="loginForm.get('password').invalid && (loginForm.get('password').dirty || loginForm.get('password').touched || submitted)">
              <label class="input-field__label" for="password">{{ 'login.PASSWORD' | translate }}</label>
              <div class="input-field__control input-field__control--icon-right">
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  id="password"
                  class="input-field__input"
                  formControlName="password"
                  [placeholder]="'login.PasswordPlaceholder' | translate"
                  required>
                <button
                  type="button"
                  class="input-field__icon-btn"
                  (click)="togglePasswordVisibility()"
                  [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
                  [attr.aria-pressed]="showPassword">
                  <i-feather [name]="showPassword ? 'eye-off' : 'eye'" class="input-field__icon"></i-feather>
                </button>
              </div>
              <p class="input-field__helper input-field__helper--error"
                *ngIf="loginForm.get('password').invalid && (loginForm.get('password').dirty || loginForm.get('password').touched || submitted)">
                <span *ngIf="loginForm.get('password').hasError('required')">{{ 'login.PasswordIsRequired' | translate }}</span>
                <span *ngIf="loginForm.get('password').hasError('password') && !loginForm.get('password').hasError('required')">
                  {{ 'login.PasswordIsValid' | translate }}
                </span>
              </p>
            </div>
          </div>

          <p class="login-card__forgot" *ngIf="!isPasswordless">
            <a class="login-card__link" [routerLink]="['/auth/reset-password']">
              {{ 'login.Forgetpassword?' | translate }}
            </a>
          </p>
        </div>

        <div class="login-card__actions">
          <button
            type="submit"
            class="btn--primary btn--l btn--block login-card__submit"
            [class.btn--loading]="isProcessing"
            [disabled]="isProcessing">
            <ng-container *ngIf="!isProcessing">{{ (isPasswordless ? 'login.SendCode' : 'login.LOGIN') | translate }}</ng-container>
            <span *ngIf="isProcessing" class="btn--loading__content">
              <i-feather name="loader" class="btn--loading__spinner" aria-hidden="true"></i-feather>
              {{ 'login.LoggingIn' | translate }}
            </span>
          </button>

          <p class="login-card__signup-prompt">
            {{ 'login.DontHaveAccount' | translate }}
            <a class="login-card__signup-link" [routerLink]="['/auth/signup']">{{ 'login.SignUp' | translate }}</a>
          </p>
        </div>
      </form>

      <!-- MFA challenge -->
      <form
        class="login-card__form"
        *ngIf="loginStep === 'mfa'"
        [formGroup]="challengeForm"
        (ngSubmit)="submitChallenge()">
        <div class="login-card__fields-block">
          <div class="login-card__fields">
            <div class="input-field"
              [class.input-field--error]="challengeForm.get('mfaCode')?.invalid && (challengeForm.get('mfaCode')?.dirty || challengeForm.get('mfaCode')?.touched || submitted)">
              <label class="input-field__label" for="mfa-code">{{ 'login.MfaCode' | translate }}</label>
              <div class="input-field__control">
                <i-feather name="shield" class="input-field__icon"></i-feather>
                <input
                  type="text"
                  id="mfa-code"
                  class="input-field__input"
                  formControlName="mfaCode"
                  maxlength="6"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  [placeholder]="'login.MfaCodePlaceholder' | translate"
                  required>
              </div>
              <p class="input-field__helper input-field__helper--error"
                *ngIf="challengeForm.get('mfaCode')?.invalid && (challengeForm.get('mfaCode')?.dirty || challengeForm.get('mfaCode')?.touched || submitted)">
                {{ 'login.MfaCodeRequired' | translate }}
              </p>
            </div>
          </div>
        </div>

        <div class="login-card__actions login-card__actions--dual">
          <button
            type="submit"
            class="btn--primary btn--l btn--block login-card__submit"
            [class.btn--loading]="isProcessing"
            [disabled]="isProcessing">
            <ng-container *ngIf="!isProcessing">{{ 'login.VerifyCode' | translate }}</ng-container>
            <span *ngIf="isProcessing" class="btn--loading__content">
              <i-feather name="loader" class="btn--loading__spinner" aria-hidden="true"></i-feather>
              {{ 'login.LoggingIn' | translate }}
            </span>
          </button>
          <button
            type="button"
            class="btn--ghost2 btn--l btn--block login-card__secondary"
            (click)="resetLoginFlow()"
            [disabled]="isProcessing">
            {{ 'login.Back' | translate }}
          </button>
        </div>
      </form>

      <!-- Email code (passwordless) -->
      <form
        class="login-card__form"
        *ngIf="loginStep === 'email_otp'"
        [formGroup]="challengeForm"
        (ngSubmit)="submitChallenge()">
        <div class="login-card__fields-block">
          <p class="login-card__hint">
            {{ 'login.EmailCodeSentTo' | translate: { email: authChallenge?.email } }}
          </p>
          <div class="login-card__fields">
            <div class="input-field"
              [class.input-field--error]="challengeForm.get('otpCode')?.invalid && (challengeForm.get('otpCode')?.dirty || challengeForm.get('otpCode')?.touched || submitted)">
              <label class="input-field__label" for="otp-code">{{ 'login.EmailCode' | translate }}</label>
              <div class="input-field__control">
                <i-feather name="mail" class="input-field__icon"></i-feather>
                <input
                  type="text"
                  id="otp-code"
                  class="input-field__input"
                  formControlName="otpCode"
                  maxlength="8"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  [placeholder]="'login.EmailCodePlaceholder' | translate"
                  (input)="onDigitsInput('otpCode', $any($event.target).value, 8)"
                  required>
              </div>
              <p class="input-field__helper input-field__helper--error"
                *ngIf="challengeForm.get('otpCode')?.invalid && (challengeForm.get('otpCode')?.dirty || challengeForm.get('otpCode')?.touched || submitted)">
                {{ 'login.EmailCodeRequired' | translate }}
              </p>
            </div>
          </div>
          <p class="login-card__forgot">
            <button
              type="button"
              class="login-card__link login-card__link-btn"
              [disabled]="resendCountdown > 0 || isProcessing"
              (click)="resendEmailCode()">
              <ng-container *ngIf="resendCountdown > 0">
                {{ 'login.ResendCodeIn' | translate: { seconds: resendCountdown } }}
              </ng-container>
              <ng-container *ngIf="resendCountdown <= 0">{{ 'login.ResendCode' | translate }}</ng-container>
            </button>
          </p>
        </div>

        <div class="login-card__actions login-card__actions--dual">
          <button
            type="submit"
            class="btn--primary btn--l btn--block login-card__submit"
            [class.btn--loading]="isProcessing"
            [disabled]="isProcessing">
            <ng-container *ngIf="!isProcessing">{{ 'login.VerifyCode' | translate }}</ng-container>
            <span *ngIf="isProcessing" class="btn--loading__content">
              <i-feather name="loader" class="btn--loading__spinner" aria-hidden="true"></i-feather>
              {{ 'login.LoggingIn' | translate }}
            </span>
          </button>
          <button
            type="button"
            class="btn--ghost2 btn--l btn--block login-card__secondary"
            (click)="resetLoginFlow()"
            [disabled]="isProcessing">
            {{ 'login.Back' | translate }}
          </button>
        </div>
      </form>

      <!-- Authenticator setup required before the session is issued -->
      <form
        class="login-card__form"
        *ngIf="loginStep === 'mfa_setup'"
        [formGroup]="challengeForm"
        (ngSubmit)="submitChallenge()">
        <div class="login-card__fields-block">
          <p class="login-card__hint">{{ 'login.MfaSetupHelp' | translate }}</p>

          <p class="login-card__hint" *ngIf="mfaSetupLoading">{{ 'TFMfa.loading' | translate }}</p>

          <div class="login-mfa-setup" *ngIf="mfaSetupSecret">
            <img
              *ngIf="mfaSetupQrDataUrl"
              class="login-mfa-setup__qr"
              [src]="mfaSetupQrDataUrl"
              [alt]="'TFMfa.qrAlt' | translate">
            <p class="login-card__hint">{{ 'TFMfa.manualEntry' | translate }}</p>
            <div class="login-mfa-setup__secret-row">
              <code class="login-mfa-setup__secret">{{ mfaSetupSecret }}</code>
              <button type="button" class="login-mfa-setup__copy" (click)="copyMfaSetupSecret()">
                <i-feather [name]="mfaSecretCopied ? 'check' : 'copy'"></i-feather>
                <span>{{ (mfaSecretCopied ? 'TFMfa.copied' : 'TFMfa.copy') | translate }}</span>
              </button>
            </div>
          </div>

          <div class="login-card__fields" *ngIf="mfaSetupSecret">
            <div class="input-field"
              [class.input-field--error]="challengeForm.get('mfaCode')?.invalid && (challengeForm.get('mfaCode')?.dirty || challengeForm.get('mfaCode')?.touched || submitted)">
              <label class="input-field__label" for="mfa-setup-code">{{ 'login.MfaCode' | translate }}</label>
              <div class="input-field__control">
                <i-feather name="shield" class="input-field__icon"></i-feather>
                <input
                  type="text"
                  id="mfa-setup-code"
                  class="input-field__input"
                  formControlName="mfaCode"
                  maxlength="6"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  [placeholder]="'login.MfaCodePlaceholder' | translate"
                  (input)="onDigitsInput('mfaCode', $any($event.target).value, 6)"
                  required>
              </div>
              <p class="input-field__helper input-field__helper--error"
                *ngIf="challengeForm.get('mfaCode')?.invalid && (challengeForm.get('mfaCode')?.dirty || challengeForm.get('mfaCode')?.touched || submitted)">
                {{ 'login.MfaCodeRequired' | translate }}
              </p>
            </div>

            <div class="input-field">
              <label class="input-field__label" for="mfa-setup-device">{{ 'TFMfa.deviceName' | translate }}</label>
              <div class="input-field__control">
                <input
                  type="text"
                  id="mfa-setup-device"
                  class="input-field__input"
                  formControlName="deviceName"
                  [placeholder]="'TFMfa.deviceNamePlaceholder' | translate">
              </div>
            </div>
          </div>
        </div>

        <div class="login-card__actions login-card__actions--dual">
          <button
            type="submit"
            class="btn--primary btn--l btn--block login-card__submit"
            [class.btn--loading]="isProcessing"
            [disabled]="isProcessing || !mfaSetupSecret">
            <ng-container *ngIf="!isProcessing">{{ 'login.VerifyCode' | translate }}</ng-container>
            <span *ngIf="isProcessing" class="btn--loading__content">
              <i-feather name="loader" class="btn--loading__spinner" aria-hidden="true"></i-feather>
              {{ 'login.LoggingIn' | translate }}
            </span>
          </button>
          <button
            type="button"
            class="btn--ghost2 btn--l btn--block login-card__secondary"
            (click)="resetLoginFlow()"
            [disabled]="isProcessing">
            {{ 'login.Back' | translate }}
          </button>
        </div>
      </form>

      <!-- New password required -->
      <form
        class="login-card__form"
        *ngIf="loginStep === 'new_password'"
        [formGroup]="challengeForm"
        (ngSubmit)="submitChallenge()">
        <div class="login-card__fields-block">
          <div class="login-card__fields">
            <div class="input-field"
              [class.input-field--error]="challengeForm.get('newPassword')?.invalid && (challengeForm.get('newPassword')?.dirty || challengeForm.get('newPassword')?.touched || submitted)">
              <label class="input-field__label" for="new-password">{{ 'TFChangePassword.NewPassword' | translate }}</label>
              <div class="input-field__control input-field__control--icon-right">
                <input
                  [type]="showChallengePassword ? 'text' : 'password'"
                  id="new-password"
                  class="input-field__input"
                  formControlName="newPassword"
                  [placeholder]="'login.PasswordPlaceholder' | translate"
                  required>
                <button
                  type="button"
                  class="input-field__icon-btn"
                  (click)="toggleChallengePasswordVisibility()"
                  [attr.aria-label]="showChallengePassword ? 'Hide password' : 'Show password'"
                  [attr.aria-pressed]="showChallengePassword">
                  <i-feather [name]="showChallengePassword ? 'eye-off' : 'eye'" class="input-field__icon"></i-feather>
                </button>
              </div>
              <p class="input-field__helper input-field__helper--error" *ngIf="showNewPasswordError()">
                <span *ngIf="challengeForm.get('newPassword')?.hasError('required')">
                  {{ 'TFChangePassword.NewPasswordIsRequired' | translate }}
                </span>
                <ng-container *ngIf="!challengeForm.get('newPassword')?.hasError('required')">
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('minlength')">
                    {{ 'TFChangePassword.PasswordMinLength' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordUppercase')">
                    {{ 'TFChangePassword.PasswordMustContainUppercase' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordLowercase')">
                    {{ 'TFChangePassword.PasswordMustContainLowercase' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordDigit')">
                    {{ 'TFChangePassword.PasswordMustContainDigit' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordSpecial')">
                    {{ 'TFChangePassword.PasswordMustContainSpecial' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordWhitespace')">
                    {{ 'TFChangePassword.PasswordMustNotContainWhitespace' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordEmailLocalPart')">
                    {{ 'TFChangePassword.PasswordMustNotContainEmail' | translate }}
                  </span>
                  <span *ngIf="challengeForm.get('newPassword')?.hasError('passwordRepeated')">
                    {{ 'TFChangePassword.PasswordMustNotBeRepeated' | translate }}
                  </span>
                </ng-container>
              </p>
            </div>

            <div class="input-field"
              [class.input-field--error]="challengeForm.get('confirmPassword')?.invalid && (challengeForm.get('confirmPassword')?.dirty || challengeForm.get('confirmPassword')?.touched || submitted)">
              <label class="input-field__label" for="confirm-password">{{ 'TFChangePassword.ConfirmPassword' | translate }}</label>
              <div class="input-field__control input-field__control--icon-right">
                <input
                  [type]="showChallengeConfirmPassword ? 'text' : 'password'"
                  id="confirm-password"
                  class="input-field__input"
                  formControlName="confirmPassword"
                  [placeholder]="'login.ConfirmPasswordPlaceholder' | translate"
                  required>
                <button
                  type="button"
                  class="input-field__icon-btn"
                  (click)="toggleChallengeConfirmPasswordVisibility()"
                  [attr.aria-label]="showChallengeConfirmPassword ? 'Hide password' : 'Show password'"
                  [attr.aria-pressed]="showChallengeConfirmPassword">
                  <i-feather [name]="showChallengeConfirmPassword ? 'eye-off' : 'eye'" class="input-field__icon"></i-feather>
                </button>
              </div>
              <p class="input-field__helper input-field__helper--error" *ngIf="showConfirmPasswordError()">
                <span *ngIf="challengeForm.get('confirmPassword')?.hasError('required')">
                  {{ 'TFChangePassword.ConfirmPasswordIsRequired' | translate }}
                </span>
                <span *ngIf="challengeForm.get('confirmPassword')?.hasError('mustMatch') && !challengeForm.get('confirmPassword')?.hasError('required')">
                  {{ 'TFChangePassword.MismatchedPasswordTryAgain' | translate }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div class="login-card__actions login-card__actions--dual">
          <button
            type="submit"
            class="btn--primary btn--l btn--block login-card__submit"
            [class.btn--loading]="isProcessing"
            [disabled]="isProcessing">
            <ng-container *ngIf="!isProcessing">{{ 'login.SetNewPassword' | translate }}</ng-container>
            <span *ngIf="isProcessing" class="btn--loading__content">
              <i-feather name="loader" class="btn--loading__spinner" aria-hidden="true"></i-feather>
              {{ 'login.LoggingIn' | translate }}
            </span>
          </button>
          <button
            type="button"
            class="btn--ghost2 btn--l btn--block login-card__secondary"
            (click)="resetLoginFlow()"
            [disabled]="isProcessing">
            {{ 'login.Back' | translate }}
          </button>
        </div>
      </form>

      <div class="copyright-footer">
        <p>{{ 'auth.PoweredBy' | translate }} <a [href]="zumConnectPortalUrl" target="_blank" rel="noopener">ZUMConnect</a></p>
      </div>
    </div>

    <!-- Something went wrong -->
    <app-wrong
      *ngIf="foundError && !serverUnavailable"
      titleKey="login.SomethingWentWrong"
      subtitleKey="login.UnexpectedError"
      primaryLabelKey="login.TryAgain"
      secondaryLabelKey="login.BackToLogin"
      [portalUrl]="zumConnectPortalUrl"
      (primaryAction)="tryAgain()"
      (secondaryAction)="dismissWrong()">
    </app-wrong>

    <!-- Please wait — shown after delay if request still pending -->
    <app-please-wait
      *ngIf="showPleaseWait"
      titleKey="login.PleaseWait"
      subtitleKey="login.ProcessingRequest"
      [portalUrl]="zumConnectPortalUrl">
    </app-please-wait>

    <!-- Server unavailable -->
    <app-server-unavailable
      *ngIf="serverUnavailable"
      titleKey="login.ServerUnavailable"
      subtitleKey="login.ServerUnavailableMessage"
      primaryLabelKey="login.Retry"
      secondaryLabelKey="login.BackToLogin"
      [portalUrl]="zumConnectPortalUrl"
      (primaryAction)="retryLogin()"
      (secondaryAction)="dismissServerUnavailable()">
    </app-server-unavailable>
  </div>
</div>
